import Link from 'next/link';
import { useRouter } from 'next/router';
import { Fragment, HTMLAttributes, useEffect, useMemo, useState } from 'react';
import QuoteCreationMethodStatusView from '@/cms/components/Quote/quoteCreationMethod/status';
import QuoteRenewalStatusView from '@/cms/components/statusView/quote/renewalStatus';
import QuoteStatusView from '@/cms/components/statusView/quote/status';
import ValidationStatusView from '@/cms/components/statusView/validation/validationStatus';
import { useQuoteDetailStore } from '@/cms/store/quote/QuoteDetail';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/outline';
import {
  InsuranceType,
  LoadingMask,
  PROVIDER_SOURCE,
  QUOTE_PURCHASE_PROBABILITY_MAP,
  QuoteResponse,
  SelectAsyncInput,
  SelectOptionType,
  apiUpdateAdminQuote,
  convertCamelCaseToUnderscore,
  fetchAssigneeUsers,
  formatCommonDateStr,
  formatCurrency,
  formatDate,
  formatSnakeCase,
  getDaysDiffFromToday,
  getJoveCountryCurrency,
  getQuoteSourceDisplayText,
  getTruelyValueMap,
  toCapitalCase,
  truelyValueMap,
  useCountryStore,
  useLocalTranslation,
} from '@jove-frontend/uikit';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import FirstPageOutlinedIcon from '@mui/icons-material/FirstPageOutlined';
import LastPageOutlinedIcon from '@mui/icons-material/LastPageOutlined';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { PaginationActionType, SortOrder, TCatalogStore, TPageSize } from '../../store/catalog/CatalogStore';
import { useQuoteStore } from '../../store/quote/QuoteStore';
import { IQuoteCatalogState } from '../../store/quote/types';
import { useUsersStore } from '../../store/user/UserStore';
import { Routes } from '../../util/routes';
import CopyQuoteLink from './CopyQuoteLink';

const TableHeader: React.FC<{
  column: any;
  isSortable: boolean;
  onSort: () => void;
  isSorted: boolean;
  sortOrder: SortOrder | null;
  className?: string;
}> = ({ column, isSortable, onSort, isSorted, sortOrder, className }) => {
  const { $t } = useLocalTranslation();

  return (
    <th
      key={column.name}
      className={clsx('p-3 text-left', {
        'w-[80px]': column.name === 'id',
        'w-[120px]': column.name === 'created_at' || column.name === 'updated_at',
        'w-[160px]': column.name === 'providerCode',
        'w-[240px]': column.name === 'status',
      })}
    >
      <button className="flex items-center justify-start" onClick={onSort} disabled={!isSortable}>
        <span className="p-small text-bold mr-2 font-semibold">{column.labelKey ? $t(column.labelKey) : column.label}</span>
        {isSorted &&
          (sortOrder === SortOrder.asc ? (
            <ChevronUpIcon width={12} className="flex-shrink-0" />
          ) : (
            <ChevronDownIcon width={12} className="flex-shrink-0" />
          ))}
      </button>
    </th>
  );
};

type Props = HTMLAttributes<HTMLDivElement> & {
  store: TCatalogStore<QuoteResponse & { assignee: any; organization: any }>;
  catalogColumns: IQuoteCatalogState['catalogColumns'];
  isAdminControlPanel?: boolean;
};

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

export const QuoteTable = ({ store, catalogColumns, className }: Props) => {
  const { $t } = useLocalTranslation();
  const router = useRouter();
  const {
    catalogState: { collection, total_count, offset, pageSize, page, isLoading },
    catalogActions: { fetch, handlePaginationChange },
    sortState,
    sortActions: { setSort },
  } = store;

  const { getInsuranceProvider, fetchInsuranceProviders } = useQuoteStore(({ actions }) => actions);
  const { initialize: initializeUsersStore } = useUsersStore(({ actions }) => actions);
  const [{ countries, currencies }] = useCountryStore(({ state }) => [state]);
  const pageSizeFromQuery = router.query.pageSize as string;

  useEffect(() => {
    fetchInsuranceProviders();
    initializeUsersStore();

    if (router.query.page || router.query.pageSize) {
      handlePaginationChange({
        type: PaginationActionType.INITIALIZE,
        page: (parseInt(router.query.page as string) as number) || 1,
        pageSize: (parseInt(pageSizeFromQuery) as TPageSize) || 10,
      });
    }
  }, []);

  const onChangeAssignee = useMemo(
    () =>
      async ({ quote, value, updateKey }: { quote: any; value: SelectOptionType; updateKey: string }) => {
        await apiUpdateAdminQuote(quote?.id, {
          [updateKey]: value as string,
        }).then(() => fetch());
      },
    [fetch]
  );

  function nextPageAction() {
    handlePaginationChange({
      type: PaginationActionType.NEXT,
      router,
    });
  }

  function previousPageAction() {
    handlePaginationChange({
      type: PaginationActionType.PREVIOUS,
      router,
    });
  }

  function changePageSizeAction(pageSize: string) {
    handlePaginationChange({
      type: PaginationActionType.SIZE,
      pageSize: parseInt(pageSize) as TPageSize,
      router,
    });
  }

  function firstPageAction() {
    handlePaginationChange({
      type: PaginationActionType.FIRST,
      router,
    });
  }

  function lastPageAction() {
    handlePaginationChange({
      type: PaginationActionType.LAST,
      router,
    });
  }

  const getColumnSortFieldName = (name) => {
    let realName = name;
    switch (name) {
      case 'emailOpenRate':
        realName = 'email_viewed_times';
        break;
      case 'emailClickRate':
        realName = 'quote_viewed_times';
        break;
      default:
        realName = convertCamelCaseToUnderscore(name);
        break;
    }
    return realName;
  };

  return (
    <div className={clsx('relative mb-3', className)}>
      <table className="w-full table-fixed border-collapse">
        <thead>
          <tr className="bg-slate-500/15">
            {catalogColumns.map((column, index) => {
              const isFirstColumn = index === 0;
              const isLastColumn = index === catalogColumns.length - 1;

              return (
                <TableHeader
                  key={column.name}
                  column={column}
                  isSortable={column.isSortable}
                  onSort={() => setSort(getColumnSortFieldName(column.name))}
                  isSorted={sortState.column === getColumnSortFieldName(column.name)}
                  sortOrder={sortState.order}
                  className={clsx({
                    'rounded-tl-lg': isFirstColumn,
                    'rounded-tr-lg': isLastColumn,
                  })}
                />
              );
            })}
          </tr>
        </thead>

        <tbody>
          {!collection ||
            (collection.length === 0 && (
              <tr>
                <td colSpan={catalogColumns.length} className="p-4 text-center">
                  {$t('common:no_results')}
                </td>
              </tr>
            ))}

          {collection?.map((quote, index) => {
            const { git, meta, providerCode } = quote.payload || {};
            const isGit = !!git || quote.insuranceType === InsuranceType.GOODS_IN_TRANSIT_INSURANCE;
            let url = '';

            if (localStorage.getItem('test-chubb') === 'true') {
              url = isGit ? Routes.quoteGoodsInTransitPage(quote.id) : Routes.quoteUniversalDetailPage(quote.id);
            } else {
              if (
                !!(meta as any)?.newQuoteFlowType ||
                [
                  PROVIDER_SOURCE().CHUBB.value,
                  PROVIDER_SOURCE().ACCELERANT.value,
                  PROVIDER_SOURCE().RECRUITMENT.value,
                  PROVIDER_SOURCE().ACCELERANT_GIT.value,
                  PROVIDER_SOURCE().GREATEAMERICAN.value,
                  PROVIDER_SOURCE().GREATEAMERICAN_BUSINESS_INSURANCE.value,
                  PROVIDER_SOURCE().HISCOX_US.value,
                ].includes(providerCode) ||
                !providerCode
              ) {
                url = Routes.quoteUniversalDetailPage(quote.id);
              } else {
                url = isGit ? Routes.quoteGoodsInTransitPage(quote.id) : Routes.quoteBusinessInsurancePage(quote.id);
              }
            }

            const status = quote?.status;

            // let insurerText = getInsuranceProvider(quote.payload.providerCode)?.name || '-';
            let insurerText = quote.payload['insurerText'] || '-';

            // Currency
            const currency = getJoveCountryCurrency({
              country: quote.payload?.businessCountry,
              countries,
            });

            // Cell renderer per column; cells are rendered following the catalogColumns order
            const cellRenderMap: Record<string, () => JSX.Element> = {
              id: () => (
                <td className="id p-3">
                  <Link href={url} legacyBehavior>
                    <p className="p-small cursor-pointer truncate font-normal group-hover:text-black/70">{quote.id}</p>
                  </Link>
                </td>
              ),
              quote_creation_method: () => (
                <td className="text-center">
                  <QuoteCreationMethodStatusView className="inline-flex" status={quote?.['fastQuoteStates']} />
                </td>
              ),
              source: () => (
                <td className="text-center">
                  <p>{getQuoteSourceDisplayText(quote.source, quote.payload)}</p>
                </td>
              ),
              created_at: () => (
                <td className="created-at p-3">
                  <Link href={url} legacyBehavior>
                    <p className="p-small cursor-pointer truncate font-normal group-hover:text-black/70">
                      {quote.createdAt &&
                        formatDate({
                          date: quote.createdAt,
                          options: { month: 'short' },
                        })}
                    </p>
                  </Link>
                </td>
              ),
              updated_at: () => (
                <td className="updated-at p-3">
                  <Link href={url} legacyBehavior>
                    <p className="p-small cursor-pointer truncate font-normal group-hover:text-black/70">
                      {quote.updatedAt &&
                        formatDate({
                          date: quote.updatedAt,
                          options: { month: 'short' },
                        })}
                    </p>
                  </Link>
                </td>
              ),
              purchased_at: () => (
                <td className="updated-at p-3">
                  <Link href={url} legacyBehavior>
                    <p className="p-small cursor-pointer truncate font-normal group-hover:text-black/70">{(quote as any)?.purchasedAtText}</p>
                  </Link>
                </td>
              ),
              companyName: () => (
                <td className="email p-3">
                  <Link href={url} legacyBehavior>
                    <p className="p-small cursor-pointer truncate font-normal group-hover:text-black/70">{quote.payload?.companyName}</p>
                  </Link>
                </td>
              ),
              firstName: () => (
                <td className="customer-name p-3">
                  <Link href={url} legacyBehavior>
                    <p className="p-small cursor-pointer truncate font-normal group-hover:text-black/70">
                      {quote.payload.firstName + ' ' + quote.payload.lastName}
                    </p>
                  </Link>
                </td>
              ),
              email: () => (
                <td className="email p-3">
                  <Link href={url} legacyBehavior>
                    <p className="p-small cursor-pointer truncate font-normal group-hover:text-black/70">{quote.payload.email}</p>
                  </Link>
                </td>
              ),
              phone: () => (
                <td className="email p-3">
                  <Link href={url} legacyBehavior>
                    <p className="p-small cursor-pointer truncate font-normal group-hover:text-black/70">{quote.payload.phone}</p>
                  </Link>
                </td>
              ),
              insuranceType: () => (
                <td className="email p-3">
                  <Link href={url} legacyBehavior>
                    <p className="p-small cursor-pointer truncate font-normal group-hover:text-black/70">
                      {quote.payload.insuranceType ? toCapitalCase(formatSnakeCase(quote.payload.insuranceType)) : '-'}
                    </p>
                  </Link>
                </td>
              ),
              assigneeId: () => (
                <td className="assigned-to p-3">
                  <SelectAsyncInput
                    value={{
                      value: quote?.assigneeId ?? null,
                      label: quote?.assigneeId ? quote?.assignee?.first_name + ' ' + quote?.assignee?.last_name : $t('common:unassigned'),
                    }}
                    fetch={() => fetchAssigneeUsers({ organizationSlug: 'joveco' })}
                    onChange={({ value }: { value: SelectOptionType }) => onChangeAssignee({ quote, value, updateKey: 'assigneeId' })}
                  />
                </td>
              ),
              referral_assignee_id: () => (
                <td className="assigned-to p-3">
                  <SelectAsyncInput
                    value={{
                      value: quote?.['referralAssigneeId'] ?? null,
                      label: quote?.['referralAssigneeId']
                        ? quote?.['referralAssignee']?.first_name + ' ' + quote?.['referralAssignee']?.last_name
                        : $t('common:unassigned'),
                    }}
                    fetch={() => fetchAssigneeUsers({ organizationSlug: 'joveco' })}
                    onChange={({ value }: { value: SelectOptionType }) => onChangeAssignee({ quote, value, updateKey: 'referralAssigneeId' })}
                  />
                </td>
              ),
              providerCode: () => (
                <td className="email p-3">
                  <Link href={url} legacyBehavior>
                    <p className="p-small cursor-pointer truncate font-normal group-hover:text-black/70">{insurerText}</p>
                  </Link>
                </td>
              ),
              purchaseProbability: () => (
                <td className="email p-3">
                  <Link href={url} legacyBehavior>
                    <p className="p-small cursor-pointer truncate font-normal group-hover:text-black/70">
                      {QUOTE_PURCHASE_PROBABILITY_MAP()[quote.payload.purchaseProbability]?.label}
                    </p>
                  </Link>
                </td>
              ),
              quoteNumber: () => (
                <td className="email p-3">
                  <p className="p-small cursor-pointer truncate font-normal group-hover:text-black/70">{quote.quoteNumber}</p>
                </td>
              ),
              policyNumber: () => (
                <td className="email p-3">
                  <p className="p-small cursor-pointer truncate font-normal group-hover:text-black/70">{quote.payload?.policyNumber}</p>
                </td>
              ),
              partnerName: () => (
                <td className="email p-3">
                  <Link href={url} legacyBehavior>
                    <p className="p-small cursor-pointer truncate font-normal group-hover:text-black/70">{quote['partnerName']}</p>
                  </Link>
                </td>
              ),
              partnerReferralFee: () => (
                <td className="email p-3">
                  <Link href={url} legacyBehavior>
                    <p className="p-small cursor-pointer truncate font-normal group-hover:text-black/70">
                      {quote.payload?.aggregatePrices?.broker_commission_amount
                        ? formatCurrency({
                            amount: Number(quote.payload?.aggregatePrices?.broker_commission_amount),
                            currency,
                          })
                        : '-'}
                    </p>
                  </Link>
                </td>
              ),
              partnerFee: () => (
                <td className="email p-3">
                  <Link href={url} legacyBehavior>
                    <p className="p-small cursor-pointer truncate font-normal group-hover:text-black/70">
                      {quote.payload?.aggregatePrices?.broker_fee_amount
                        ? formatCurrency({
                            amount: Number(quote.payload?.aggregatePrices?.broker_fee_amount),
                            currency,
                          })
                        : '-'}
                    </p>
                  </Link>
                </td>
              ),
              region: () => (
                <td className="email text-center">
                  <Link href={url} legacyBehavior>
                    <p className="p-small cursor-pointer truncate font-normal group-hover:text-black/70">
                      {(countries || []).find((item) => item.code === (quote.payload.businessCountry || quote.payload.country))?.name}
                    </p>
                  </Link>
                </td>
              ),
              contractStartDate: () => (
                <td className="status p-3">
                  <p>{quote.payload?.projectPeriodStart ? formatCommonDateStr(quote.payload?.projectPeriodStart) : '-'}</p>
                </td>
              ),
              property_analysis_not_available: () => (
                <td className="status p-3">
                  <p>{!!quote.payload?.['propertyAnalysisNotAvailable'] ? getTruelyValueMap().yes.label : getTruelyValueMap().no.label}</p>
                </td>
              ),
              sanction_check_referral: () => (
                <td className="status p-3">
                  <p>{!!quote.payload?.['propertyAnalysisNotAvailable'] ? getTruelyValueMap().yes.label : getTruelyValueMap().no.label}</p>
                </td>
              ),
              aggregate_price: () => (
                <td className="status p-3">
                  <p>
                    {quote.payload?.aggregatePrices?.total
                      ? formatCurrency({
                          amount: Number(quote.payload?.aggregatePrices?.total),
                          currency,
                        })
                      : '-'}
                  </p>
                </td>
              ),
              renewalStatus: () => (
                <td className="status p-3">
                  <QuoteRenewalStatusView className="inline-flex" status={quote.renewStatus} />
                </td>
              ),
              organizationName: () => (
                <td className="email p-3">
                  <Link href={url} legacyBehavior>
                    <p className="p-small cursor-pointer truncate font-normal group-hover:text-black/70">
                      {quote.payload && quote.payload.meta ? (quote.payload.meta as any)?.org_id : '-'}
                    </p>
                  </Link>
                </td>
              ),
              emailOpenRate: () => (
                <td className="text-center">
                  <Link href={url} legacyBehavior>
                    <p className="p-small cursor-pointer truncate font-normal group-hover:text-black/70">{quote?.['emailViewedTimes'] || 0}</p>
                  </Link>
                </td>
              ),
              emailClickRate: () => (
                <td className="text-center">
                  <Link href={url} legacyBehavior>
                    <p className="p-small cursor-pointer truncate font-normal group-hover:text-black/70">{quote?.['quoteViewedTimes'] || 0}</p>
                  </Link>
                </td>
              ),
              policy_start_date: () => (
                <td className="text-center">
                  <p>{!!quote.payload?.projectPeriodStart ? formatCommonDateStr(quote.payload?.projectPeriodStart) : ''}</p>
                </td>
              ),
              policy_expiry_date: () => (
                <td className="text-center">
                  <p>{quote['existPolicyEndDate']}</p>
                </td>
              ),
              policyExpirationDays: () => (
                <td className="text-center">
                  <p>{getDaysDiffFromToday(quote['existPolicyEndDate'])}</p>
                </td>
              ),
              validation_status: () => (
                <td className="status p-3">
                  <ValidationStatusView className="inline-flex" status={(quote as any)?.validationStatus} />
                </td>
              ),
              status: () => (
                <td className="status p-3">
                  <QuoteStatusView className="inline-flex" status={status} />
                </td>
              ),
              duplicateQuote: () => (
                <td className="status p-3">
                  <p>{(quote as any)?.duplicationInfo?.is_duplicated ? truelyValueMap.yes.label : truelyValueMap.no.label}</p>
                </td>
              ),
              copyURL: () => (
                <td className="status p-3">
                  <CopyQuoteLink quoteId={quote.id} source={quote.source} status={quote.status} fastQuoteStates={quote.fastQuoteStates} />
                </td>
              ),
            };

            return (
              <tr key={quote['uniqueId'] + index} className="group border border-t-0 border-black/5 text-left transition-colors hover:bg-slate-500/5">
                {catalogColumns.map((column) => {
                  return <Fragment key={column.name}>{cellRenderMap[column.name]?.()}</Fragment>;
                })}
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={Math.floor(catalogColumns.length / 2)}>
              <span className="p-small ml-5 font-semibold text-black/70">
                {offset + 1} - {offset + pageSize > total_count ? total_count : offset + pageSize} {$t('text:of')} {total_count}
              </span>
            </td>
            <td colSpan={Math.ceil(catalogColumns.length / 2)} className="justify-between p-3 pt-6">
              <div className="flex items-center justify-end">
                <span className="p-small mr-1 font-semibold text-black/70">{$t('common:rows_per_page')}: </span>
                <span className="p-small text-black">
                  <select
                    className="ml-1 cursor-pointer rounded-full border-black/20 py-2 pr-7 text-center text-sm font-semibold text-black transition-colors duration-300 hover:border-black hover:bg-black/5 focus:outline-none"
                    onChange={(e) => changePageSizeAction(e.target.value)}
                    defaultValue={pageSizeFromQuery}
                  >
                    {PAGE_SIZE_OPTIONS.map((item) => (
                      <option key={item} value={item}>
                        {item}
                      </option>
                    ))}
                  </select>
                </span>
                <div className="ml-5 flex space-x-2">
                  <button
                    className="rounded-full border border-black/10 p-1 transition-colors hover:border-black/30 hover:bg-black/5 disabled:border-black/10 disabled:text-black/10"
                    disabled={offset === 0}
                    onClick={firstPageAction}
                  >
                    <FirstPageOutlinedIcon />
                  </button>
                  <button
                    className="rounded-full border border-black/10 p-1 transition-colors hover:border-black/30 hover:bg-black/5 disabled:border-black/10 disabled:text-black/10"
                    disabled={offset === 0}
                    onClick={previousPageAction}
                  >
                    <ChevronLeftRoundedIcon />
                  </button>
                  <div className="flex items-center justify-center px-2">
                    <span className="p-small font-semibold text-black/70">
                      {$t('common:page_of', {
                        page: page,
                        size: Math.ceil(total_count / pageSize),
                      })}
                    </span>
                  </div>
                  <button
                    className="rounded-full border border-black/10 p-1 transition-colors hover:border-black/30 hover:bg-black/5 disabled:border-black/10 disabled:text-black/10"
                    disabled={offset + pageSize >= total_count}
                    onClick={nextPageAction}
                  >
                    <ChevronRightRoundedIcon />
                  </button>
                  <button
                    className="rounded-full border border-black/10 p-1 transition-colors hover:border-black/30 hover:bg-black/5 disabled:border-black/10 disabled:text-black/10"
                    disabled={offset + pageSize >= total_count}
                    onClick={lastPageAction}
                  >
                    <LastPageOutlinedIcon />
                  </button>
                </div>
              </div>
            </td>
          </tr>
        </tfoot>
      </table>

      {isLoading && <LoadingMask />}
    </div>
  );
};
